import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureFlagTypes, selectIsEnabled } from 'common/features/store/duck/ffs';
import { selectIsCoordinator, selectUserId } from 'common/features/store/duck/account/duck';
import { selectSelectedOrgId } from 'common/features/store/duck/home/duck';
import { posthog } from 'config/posthog';
import { useRootSelector } from 'rootStore';
import { Routes } from 'routes';
import { identifyPosthogUser, viewedPersonProperties } from '../analytics/posthogPerson';

export const COORDINATOR_DASHBOARD_PAGE_VIEWED = 'coordinator_dashboard:page_viewed';

export type CoordinatorDashboardPage = 'home' | 'participant_accounts' | 'order_history' | 'other';

const PAGE_BY_PATH: Partial<Record<string, CoordinatorDashboardPage>> = {
    [Routes.CoordinatorDashboardHome]: 'home',
    [Routes.CoordinatorDashboardParticipantAccounts]: 'participant_accounts',
    [Routes.CoordinatorDashboardOrderHistory]: 'order_history',
};

const DASHBOARD_PATHS = new Set<string>(
    Object.values(Routes).filter((route) => route.startsWith(`${Routes.CoordinatorDashboardRoot}/`))
);

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '');

// Null for paths the dashboard Switch redirects (root, unknown), so a pending redirect never counts.
export const toCoordinatorDashboardPage = (pathname: string): CoordinatorDashboardPage | null => {
    const path = normalizePath(pathname);
    return DASHBOARD_PATHS.has(path) ? PAGE_BY_PATH[path] ?? 'other' : null;
};

export const useCoordinatorDashboardPageView = () => {
    const { pathname } = useLocation();
    const userId = useRootSelector(selectUserId);
    const isCoordinator = useRootSelector(selectIsCoordinator);
    const selectedOrgId = useRootSelector(selectSelectedOrgId);
    const isDashboardEnabled = useRootSelector((state) =>
        selectIsEnabled(state, FeatureFlagTypes.enable_unified_coordinator_dashboard)
    );
    // Keyed by pathname only, so query-string changes on the same route never re-fire.
    const lastTrackedPath = useRef<string | null>(null);

    useEffect(() => {
        const page = toCoordinatorDashboardPage(pathname);
        const path = normalizePath(pathname);
        if (!page) {
            lastTrackedPath.current = null;
            return;
        }
        if (
            !userId ||
            !isCoordinator ||
            !isDashboardEnabled ||
            !posthog?.capture ||
            lastTrackedPath.current === path
        ) {
            return;
        }
        lastTrackedPath.current = path;
        identifyPosthogUser(userId);
        const { $set, $set_once } = viewedPersonProperties('coord_dash');
        posthog.capture(COORDINATOR_DASHBOARD_PAGE_VIEWED, {
            page,
            user_id: userId,
            organization_id: selectedOrgId || null,
            platform: 'web',
            $set: { ...$set, is_coordinator: true },
            $set_once,
        });
    }, [pathname, userId, isCoordinator, isDashboardEnabled, selectedOrgId]);
};
